import { useState, useEffect, useCallback } from 'react'
import { notificationService, TopicSubscription, AVAILABLE_TOPICS } from '@/lib/notifications'
import { OfflineCache, UserPreferences } from '@/lib/offline-cache'

export interface NotificationState {
  permission: NotificationPermission
  token: string | null
  subscribedTopics: string[]
  isLoading: boolean
  error: string | null
}

export function useNotifications(userId?: string) {
  const [state, setState] = useState<NotificationState>({
    permission: 'default',
    token: null,
    subscribedTopics: [],
    isLoading: false,
    error: null
  })

  const [userPreferences, setUserPreferences] = useState<UserPreferences | null>(null)

  // Load user preferences from IndexedDB
  useEffect(() => {
    if (userId) {
      OfflineCache.getUserPreferences(userId).then(setUserPreferences)
    }
  }, [userId])

  // Request notification permission and get token
  const initializeNotifications = useCallback(async () => {
    setState(prev => ({ ...prev, isLoading: true, error: null }))

    try {
      // Request permission
      const permission = await notificationService.requestPermission()
      setState(prev => ({ ...prev, permission }))

      if (permission !== 'granted') {
        throw new Error('Notification permission not granted')
      }

      // Get FCM token
      const token = await notificationService.getToken()
      setState(prev => ({ ...prev, token }))

      // Load subscribed topics from user preferences
      if (userPreferences) {
        setState(prev => ({ 
          ...prev, 
          subscribedTopics: userPreferences.subscribedTopics || [] 
        }))
      }

      // Setup message listener
      notificationService.setupMessageListener((payload) => {
        console.log('Notification received:', payload)
        
        // Handle notification click
        if (payload.data?.action === 'click') {
          notificationService.handleNotificationClick(payload)
        }
      })

    } catch (error) {
      console.error('Error initializing notifications:', error)
      setState(prev => ({ 
        ...prev, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      }))
    } finally {
      setState(prev => ({ ...prev, isLoading: false }))
    }
  }, [userPreferences])

  // Subscribe to a topic
  const subscribeToTopic = useCallback(async (topic: string) => {
    try {
      const success = await notificationService.subscribeToTopic(topic)
      
      if (success) {
        setState(prev => ({
          ...prev,
          subscribedTopics: [...prev.subscribedTopics, topic]
        }))

        // Update user preferences
        if (userId) {
          const updatedPrefs = {
            ...userPreferences,
            subscribedTopics: [...(userPreferences?.subscribedTopics || []), topic]
          } as UserPreferences
          
          await OfflineCache.saveUserPreferences(updatedPrefs)
          setUserPreferences(updatedPrefs)
        }
      }
    } catch (error) {
      console.error('Error subscribing to topic:', error)
      setState(prev => ({ 
        ...prev, 
        error: error instanceof Error ? error.message : 'Subscription failed' 
      }))
    }
  }, [userId, userPreferences])

  // Unsubscribe from a topic
  const unsubscribeFromTopic = useCallback(async (topic: string) => {
    try {
      const success = await notificationService.unsubscribeFromTopic(topic)
      
      if (success) {
        setState(prev => ({
          ...prev,
          subscribedTopics: prev.subscribedTopics.filter(t => t !== topic)
        }))

        // Update user preferences
        if (userId) {
          const updatedPrefs = {
            ...userPreferences,
            subscribedTopics: (userPreferences?.subscribedTopics || []).filter(t => t !== topic)
          } as UserPreferences
          
          await OfflineCache.saveUserPreferences(updatedPrefs)
          setUserPreferences(updatedPrefs)
        }
      }
    } catch (error) {
      console.error('Error unsubscribing from topic:', error)
      setState(prev => ({ 
        ...prev, 
        error: error instanceof Error ? error.message : 'Unsubscription failed' 
      }))
    }
  }, [userId, userPreferences])

  // Toggle topic subscription
  const toggleTopicSubscription = useCallback(async (topic: string) => {
    const isSubscribed = state.subscribedTopics.includes(topic)
    
    if (isSubscribed) {
      await unsubscribeFromTopic(topic)
    } else {
      await subscribeToTopic(topic)
    }
  }, [state.subscribedTopics, subscribeToTopic, unsubscribeFromTopic])

  // Send test notification
  const sendTestNotification = useCallback(async () => {
    try {
      await notificationService.sendTestNotification()
    } catch (error) {
      console.error('Error sending test notification:', error)
      setState(prev => ({ 
        ...prev, 
        error: error instanceof Error ? error.message : 'Test notification failed' 
      }))
    }
  }, [])

  // Get available topics with subscription status
  const getAvailableTopics = useCallback((): TopicSubscription[] => {
    return AVAILABLE_TOPICS.map(topic => ({
      ...topic,
      enabled: state.subscribedTopics.includes(topic.topic)
    }))
  }, [state.subscribedTopics])

  // Initialize notifications on mount
  useEffect(() => {
    initializeNotifications()
  }, [initializeNotifications])

  return {
    ...state,
    userPreferences,
    subscribeToTopic,
    unsubscribeFromTopic,
    toggleTopicSubscription,
    sendTestNotification,
    getAvailableTopics,
    initializeNotifications
  }
}
